// GestureController.js
// Handles touch/gesture detection: long-press, multi-finger taps, pinch zoom, double-tap

import {
    LONG_PRESS_DURATION,
    DOUBLE_TAP_DELAY,
    DOUBLE_TAP_DISTANCE,
    SINGLE_TAP_TIMEOUT,
    SINGLE_TAP_MOVEMENT,
    MULTI_FINGER_TAP_TIMEOUT,
    MULTI_FINGER_TAP_MOVEMENT
} from '../constants/index.js';
import { getPointerPos, getTouchCoords, getTwoTouchCenter, getTwoTouchDistance } from '../utils/coords.js';

// ============= STATE =============
// Long-press detection
let longPressTimer = null;
let longPressTarget = null;
let longPressTouchStart = null;
let longPressTriggered = false;

// Touch multi-select modes
let touchMultiSelectMode = false;
let touchPortMultiSelectMode = false;

// Current touch count
let currentTouchCount = 0;

// Pinch zoom state
let pinchState = null;

// Two-finger pan state
let twoFingerPanState = null;

// Canvas pan state (1-finger/mouse pan)
let canvasPanState = null;

// Double-tap detection
let lastTapTime = 0;
let lastTapX = 0;
let lastTapY = 0;

// Single-finger tap tracking for double-tap detection
let singleFingerTapState = null;

// Multi-finger tap detection (for undo/redo)
let multiFingerTouchState = null;

// ============= DEPENDENCIES (injected via init) =============
let deps = {
    getViewport: null,
    zoomAtPoint: null,
    applyViewportTransform: null,
    updateZoomIndicator: null,
    renderAllCables: null,
    cancelLongPressCallback: null, // Called when long press should be cancelled
    getBoxDragState: null,
    setBoxDragState: null,
    addToDeviceSelection: null,
    toggleDeviceSelection: null,
    updateSelectionVisuals: null,
    updateSelectionButtons: null,
    handleToggleSelect: null,
    clearMultiSelection: null,
    showContextMenu: null
};

// ============= INITIALIZATION =============
export function initGestureController(dependencies) {
    deps = { ...deps, ...dependencies };
}

// ============= GETTERS =============
// getPointerPos imported from utils/coords.js and re-exported
export { getPointerPos };

export function shouldPan(e) {
    // Middle mouse button always pans
    if (e.button === 1) return true;
    
    // Spacebar + click = pan (handled separately via keyboard state)
    // Alt + click = pan
    if (e.altKey) return true;
    
    // On touch: 2-finger = always pan, 1-finger = select/interact
    // This is handled differently now - 2-finger touch bypasses this check
    // and goes directly to pan. 1-finger on canvas = box select.
    return false; // 1-finger touch should NOT pan
}

export function getCurrentTouchCount() {
    return currentTouchCount;
}

export function setCurrentTouchCount(count) {
    currentTouchCount = count;
}

export function isTouchMultiSelectMode() {
    return touchMultiSelectMode;
}

export function isTouchPortMultiSelectMode() {
    return touchPortMultiSelectMode;
}

export function isLongPressTriggered() {
    return longPressTriggered;
}

export function resetLongPressTriggered() {
    longPressTriggered = false;
}

export function getPinchState() {
    return pinchState;
}

export function getTwoFingerPanState() {
    return twoFingerPanState;
}

// ============= LONG-PRESS DETECTION =============
export function startLongPressTimer(e, target, targetType) {
    cancelLongPress();
    
    const pos = getPointerPos(e);
    longPressTouchStart = { x: pos.clientX, y: pos.clientY };
    longPressTarget = { element: target, type: targetType };
    longPressTriggered = false;
    
    longPressTimer = setTimeout(() => {
        longPressTriggered = true;
        handleLongPress(e, target, targetType);
    }, LONG_PRESS_DURATION);
}

export function cancelLongPress() {
    if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
    }
    longPressTarget = null;
    longPressTouchStart = null;
}

export function checkLongPressMovement(e) {
    if (!longPressTouchStart) return;
    
    const pos = getPointerPos(e);
    const dx = pos.clientX - longPressTouchStart.x;
    const dy = pos.clientY - longPressTouchStart.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Cancel if moved more than 10px
    if (dist > 10) {
        cancelLongPress();
    }
}

function handleLongPress(e, target, targetType) {
    // Prevent native iOS context menu from appearing
    if (e.cancelable) {
        e.preventDefault();
    }
    
    // Visual feedback
    if (target && target.classList) {
        target.classList.add('long-press-pending');
        setTimeout(() => target.classList.remove('long-press-pending'), 300);
    }
    
    const pos = getPointerPos(e);
    
    if (targetType === 'device') {
        const deviceId = target.id;
        if (deps.showContextMenu) {
            deps.showContextMenu(pos.clientX, pos.clientY, 'device', { deviceId });
        }
    } else if (targetType === 'port') {
        const portEl = target;
        const direction = portEl.dataset.direction;
        if (direction === 'output') {
            // Enter port multi-select mode
            enterTouchPortMultiSelectMode(portEl);
        }
    } else if (targetType === 'canvas') {
        if (deps.showContextMenu) {
            deps.showContextMenu(pos.clientX, pos.clientY, 'canvas', { clickX: pos.clientX, clickY: pos.clientY });
        }
    } else if (targetType === 'library-item') {
        const templateId = target.dataset.templateId;
        // Cancel any in-progress touch drag since we're showing context menu
        if (deps.cancelLibraryTouchDrag) {
            deps.cancelLibraryTouchDrag();
        }
        if (deps.showContextMenu) {
            deps.showContextMenu(pos.clientX, pos.clientY, 'library', { templateId });
        }
    }
}

// ============= TOUCH MULTI-SELECT MODE =============
export function enterTouchMultiSelectMode(deviceId) {
    touchMultiSelectMode = true;
    document.body.classList.add('touch-multiselect-active');
    
    // Ensure the device is selected (don't toggle - it may already be selected
    // from the touchstart that triggered the context menu)
    if (deps.addToDeviceSelection) {
        deps.addToDeviceSelection(deviceId);
    }
    if (deps.updateSelectionVisuals) {
        deps.updateSelectionVisuals();
    }
    if (deps.updateSelectionButtons) {
        deps.updateSelectionButtons();
    }
}

export function exitTouchMultiSelectMode() {
    touchMultiSelectMode = false;
    document.body.classList.remove('touch-multiselect-active');
}

export function handleTouchMultiSelectTap(deviceId) {
    if (touchMultiSelectMode) {
        if (deps.toggleDeviceSelection) {
            deps.toggleDeviceSelection(deviceId);
        }
        if (deps.updateSelectionVisuals) {
            deps.updateSelectionVisuals();
        }
        if (deps.updateSelectionButtons) {
            deps.updateSelectionButtons();
        }
        return true;
    }
    return false;
}

// ============= TOUCH PORT MULTI-SELECT MODE =============
export function enterTouchPortMultiSelectMode(portEl) {
    touchPortMultiSelectMode = true;
    document.body.classList.add('port-multiselect-active');
    
    // Toggle this port in selection using existing port multi-select system
    const deviceId = portEl.closest('.device').id;
    const groupIndex = parseInt(portEl.dataset.group);
    const type = portEl.dataset.type;
    const index = parseInt(portEl.dataset.index);
    
    const portInfo = { deviceId, groupIndex, type, index };
    if (deps.handleToggleSelect) {
        deps.handleToggleSelect(portInfo, portEl);
    }
}

export function exitTouchPortMultiSelectMode() {
    touchPortMultiSelectMode = false;
    document.body.classList.remove('port-multiselect-active');
    if (deps.clearMultiSelection) {
        deps.clearMultiSelection();
    }
}

export function handleTouchPortMultiSelectTap(portEl) {
    if (touchPortMultiSelectMode) {
        // Output and insert ports can be multi-selected (they can send signal)
        const direction = portEl.dataset.direction;
        if (direction !== 'output' && direction !== 'insert') {
            return false;
        }
        
        const deviceId = portEl.closest('.device').id;
        const groupIndex = parseInt(portEl.dataset.group);
        const type = portEl.dataset.type;
        const index = parseInt(portEl.dataset.index);
        
        const portInfo = { deviceId, groupIndex, type, index };
        if (deps.handleToggleSelect) {
            deps.handleToggleSelect(portInfo, portEl);
        }
        return true;
    }
    return false;
}

// ============= TOUCH EVENT HANDLERS =============
export function handleTouchStart(e) {
    currentTouchCount = e.touches.length;
    
    // Track multi-finger tap state for 2-finger and 3-finger taps
    if (e.touches.length >= 2) {
        // Clear single-finger tap state when multi-finger touch starts
        singleFingerTapState = null;
        
        const initialPositions = [];
        for (let i = 0; i < e.touches.length; i++) {
            const coords = getTouchCoords(e, i);
            if (coords) initialPositions.push(coords);
        }
        multiFingerTouchState = {
            fingerCount: e.touches.length,
            startTime: Date.now(),
            initialPositions,
            moved: false
        };
    }
    
    if (e.touches.length === 2) {
        e.preventDefault();
        
        // Cancel any ongoing 1-finger operations
        cancelLongPress();
        if (deps.getBoxDragState && deps.getBoxDragState()) {
            // Cancel box selection
            if (deps.setBoxDragState) {
                deps.setBoxDragState(null);
            }
            const selectionBox = document.getElementById('selection-box');
            if (selectionBox) selectionBox.remove();
        }
        
        const center = getTwoTouchCenter(e);
        const distance = getTwoTouchDistance(e);
        const centerX = center.x;
        const centerY = center.y;
        
        const viewport = deps.getViewport ? deps.getViewport() : { scale: 1, x: 0, y: 0 };
        
        pinchState = {
            initialDistance: distance,
            initialScale: viewport.scale,
            centerX,
            centerY,
            lastDistance: distance
        };
        
        // Also set up 2-finger pan state
        twoFingerPanState = {
            startX: centerX,
            startY: centerY,
            startViewportX: viewport.x,
            startViewportY: viewport.y
        };
    } else if (e.touches.length === 3) {
        e.preventDefault();
        
        // Cancel any ongoing operations
        cancelLongPress();
        if (deps.getBoxDragState && deps.getBoxDragState()) {
            if (deps.setBoxDragState) {
                deps.setBoxDragState(null);
            }
            const selectionBox = document.getElementById('selection-box');
            if (selectionBox) selectionBox.remove();
        }
        pinchState = null;
        twoFingerPanState = null;
    }
}

export function handleTouchMove(e) {
    // Check for movement to cancel long-press
    checkLongPressMovement(e);
    
    // Track movement for multi-finger tap detection
    if (multiFingerTouchState && e.touches.length >= 2) {
        for (let i = 0; i < e.touches.length && i < multiFingerTouchState.initialPositions.length; i++) {
            const initial = multiFingerTouchState.initialPositions[i];
            const current = getTouchCoords(e, i);
            if (!current) continue;
            const dx = current.x - initial.x;
            const dy = current.y - initial.y;
            if (Math.hypot(dx, dy) > MULTI_FINGER_TAP_MOVEMENT) {
                multiFingerTouchState.moved = true;
                break;
            }
        }
    }
    
    if (e.touches.length === 2) {
        e.preventDefault();
        const center = getTwoTouchCenter(e);
        const distance = getTwoTouchDistance(e);
        const centerX = center.x;
        const centerY = center.y;
        
        // Handle pinch zoom
        if (pinchState && deps.zoomAtPoint && deps.updateZoomIndicator) {
            const scaleChange = distance / pinchState.initialDistance;
            const newScale = pinchState.initialScale * scaleChange;
            
            deps.zoomAtPoint(centerX, centerY, newScale);
            pinchState.lastDistance = distance;
            deps.updateZoomIndicator();
        }
        
        // Handle 2-finger pan
        if (twoFingerPanState && deps.getViewport && deps.applyViewportTransform && deps.renderAllCables) {
            const dx = centerX - twoFingerPanState.startX;
            const dy = centerY - twoFingerPanState.startY;
            
            const viewport = deps.getViewport();
            viewport.x = twoFingerPanState.startViewportX + dx;
            viewport.y = twoFingerPanState.startViewportY + dy;
            
            deps.applyViewportTransform();
            deps.renderAllCables();
        }
    }
}

export function handleTouchEnd(e) {
    currentTouchCount = e.touches.length;
    cancelLongPress();
    
    // Check for multi-finger tap (undo/redo) when all fingers are lifted
    if (e.touches.length === 0 && multiFingerTouchState) {
        const elapsed = Date.now() - multiFingerTouchState.startTime;
        
        if (!multiFingerTouchState.moved && elapsed < MULTI_FINGER_TAP_TIMEOUT) {
            if (multiFingerTouchState.fingerCount === 2) {
                // 2-finger tap = undo
                if (deps.triggerUndo) {
                    deps.triggerUndo();
                }
            } else if (multiFingerTouchState.fingerCount === 3) {
                // 3-finger tap = redo
                if (deps.triggerRedo) {
                    deps.triggerRedo();
                }
            }
        }
        multiFingerTouchState = null;
    }
    
    // Check for double-tap on empty canvas (toggle fit patch)
    if (e.touches.length === 0 && singleFingerTapState) {
        if (checkDoubleTap(e)) {
            if (deps.toggleFitPatch) {
                deps.toggleFitPatch();
            }
        }
        singleFingerTapState = null;
    }
    
    if (e.touches.length < 2) {
        if ((pinchState || twoFingerPanState) && deps.saveViewport) {
            deps.saveViewport(); // Persist viewport after pinch zoom or pan
        }
        pinchState = null;
        twoFingerPanState = null;
    }
}

// ============= SINGLE-FINGER TAP TRACKING (for double-tap detection) =============
export function startSingleFingerTapTracking(e) {
    const coords = getTouchCoords(e, 0);
    if (!coords) return;
    singleFingerTapState = {
        startTime: Date.now(),
        startX: coords.x,
        startY: coords.y,
        onEmptyCanvas: true
    };
}

export function clearSingleFingerTapState() {
    singleFingerTapState = null;
}

// Check if a single-finger touch ended as a tap (quick, no movement)
function checkDoubleTap(e) {
    if (!singleFingerTapState || !singleFingerTapState.onEmptyCanvas) {
        return false;
    }
    
    const elapsed = Date.now() - singleFingerTapState.startTime;
    if (elapsed > SINGLE_TAP_TIMEOUT) {
        return false; // Took too long, not a tap
    }
    
    // Check if it moved too much (use changedTouches for touchend via getPointerPos)
    const pos = getPointerPos(e);
    const dx = pos.clientX - singleFingerTapState.startX;
    const dy = pos.clientY - singleFingerTapState.startY;
    if (Math.hypot(dx, dy) > SINGLE_TAP_MOVEMENT) {
        return false; // Moved too much, not a tap
    }
    
    // It's a tap! Check for double-tap
    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime;
    const distanceFromLastTap = Math.hypot(
        pos.clientX - lastTapX,
        pos.clientY - lastTapY
    );
    
    if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < DOUBLE_TAP_DISTANCE) {
        // Double-tap detected!
        lastTapTime = 0; // Reset to avoid triple-tap triggering
        return true;
    }
    
    // Store this tap for potential double-tap detection
    lastTapTime = now;
    lastTapX = pos.clientX;
    lastTapY = pos.clientY;
    return false;
}

// ============= CANVAS PAN (1-finger/mouse) =============
export function getCanvasPanState() {
    return canvasPanState;
}

export function startCanvasPan(e) {
    // Target validation is done by callers before calling this function
    const pos = getPointerPos(e);
    const viewport = deps.getViewport ? deps.getViewport() : { x: 0, y: 0 };
    canvasPanState = {
        startX: pos.clientX,
        startY: pos.clientY,
        startViewX: viewport.x,
        startViewY: viewport.y
    };
}

export function handleCanvasPan(e) {
    if (!canvasPanState) return;
    
    const pos = getPointerPos(e);
    const deltaX = pos.clientX - canvasPanState.startX;
    const deltaY = pos.clientY - canvasPanState.startY;
    
    if (deps.getViewport) {
        const viewport = deps.getViewport();
        viewport.x = canvasPanState.startViewX + deltaX;
        viewport.y = canvasPanState.startViewY + deltaY;
    }
    
    if (deps.applyViewportTransform) deps.applyViewportTransform();
    if (deps.renderAllCables) deps.renderAllCables();
}

export function endCanvasPan() {
    if (canvasPanState) {
        canvasPanState = null;
        if (deps.saveViewport) deps.saveViewport();
    }
}
